import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Paper,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import { formatNumber } from "../utils/helpers";

// Estados de pago de MP (todos menos approved)
export const ESTADOS_MP = {
  pending: { label: "Pendiente", color: "#ed6c02", bg: "#fff3e0" },
  in_process: { label: "En proceso", color: "#0288d1", bg: "#e1f5fe" },
  in_mediation: { label: "En mediación", color: "#7b1fa2", bg: "#f3e5f5" },
  authorized: { label: "Autorizado", color: "#1976d2", bg: "#e3f2fd" },
  rejected: { label: "Rechazado", color: "#d32f2f", bg: "#ffebee" },
  cancelled: { label: "Cancelado", color: "#616161", bg: "#eeeeee" },
  refunded: { label: "Devuelto", color: "#c2185b", bg: "#fce4ec" },
  charged_back: { label: "Contracargo", color: "#b71c1c", bg: "#ffcdd2" },
};

export const getEstadoMP = (status) =>
  ESTADOS_MP[status] || { label: status || "Desconocido", color: "#424242", bg: "#f5f5f5" };

const normalizarRef = (ref) => (ref ?? "").toString().trim();

// Mismo formato que exportToCSV de helpers (TAB + BOM), con las columnas propias de esta pestaña
export const exportNoAprobadosToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    alert("No hay datos para exportar");
    return;
  }

  const headers = [
    "Referencia",
    "Fecha creación",
    "Monto MP",
    "Estado",
    "Detalle estado",
    "Medio",
    "ID MP",
    "Tiene aprobado",
  ];

  const rows = data.map((p) => [
    `"${p.external_reference || "-"}"`,
    `"${p.date_created ? moment(p.date_created).format("DD-MM-YYYY HH:mm") : "-"}"`,
    p.transaction_amount ? parseFloat(p.transaction_amount).toFixed(2) : "-",
    `"${getEstadoMP(p.status).label}"`,
    `"${p.status_detail || "-"}"`,
    `"${p.payment_method || "-"}"`,
    `"${p.id}"`,
    p.tieneAprobado ? "Sí" : "No",
  ]);

  const csvContent = [headers.join("\t"), ...rows.map((row) => row.join("\t"))].join("\n");

  const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const headerSx = { fontWeight: 500, color: "#424242", fontSize: "0.75rem" };
const cellSx = { fontSize: "0.8rem" };

const TablaNoAprobados = ({ datos, conciliacion, cargando, error }) => {
  const [filtroEstado, setFiltroEstado] = useState(null);
  const [ocultarResueltos, setOcultarResueltos] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [datos?.length, filtroEstado, ocultarResueltos]);

  // Referencias del sistema, para indicar si el pago no aprobado tiene su contraparte en la DB
  const refsSistema = useMemo(() => {
    const soloDB = new Set();
    const coincidencias = new Set();
    (conciliacion?.soloDB || []).forEach((p) => {
      const ref = normalizarRef(p.detalle);
      if (ref) soloDB.add(ref);
    });
    (conciliacion?.coincidencias || []).forEach((c) => {
      const ref = normalizarRef(c.db?.detalle);
      if (ref) coincidencias.add(ref);
    });
    return { soloDB, coincidencias };
  }, [conciliacion]);

  const getEnSistema = (mp) => {
    const ref = normalizarRef(mp.external_reference);
    if (!ref) return null;
    if (refsSistema.soloDB.has(ref)) return "soloDB";
    if (refsSistema.coincidencias.has(ref)) return "coincidencia";
    return null;
  };

  // Conteo por estado sobre los datos ya filtrados por búsqueda/monto
  const conteoPorEstado = useMemo(() => {
    const conteo = {};
    (datos || []).forEach((p) => {
      conteo[p.status] = (conteo[p.status] || 0) + 1;
    });
    return conteo;
  }, [datos]);

  const datosVisibles = useMemo(() => {
    return (datos || [])
      .filter((p) => !filtroEstado || p.status === filtroEstado)
      .filter((p) => !ocultarResueltos || !p.tieneAprobado)
      .sort((a, b) => (b.date_created || "").localeCompare(a.date_created || ""));
  }, [datos, filtroEstado, ocultarResueltos]);

  const datosPaginados = datosVisibles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const totalMonto = datosVisibles.reduce(
    (sum, p) => sum + (parseFloat(p.transaction_amount) || 0),
    0,
  );

  if (cargando) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <CircularProgress size={32} sx={{ color: "#757575" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 1 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          <Chip
            label={`Todos (${(datos || []).length})`}
            size="small"
            onClick={() => setFiltroEstado(null)}
            variant={filtroEstado === null ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />
          {Object.keys(conteoPorEstado).map((status) => {
            const estado = getEstadoMP(status);
            const activo = filtroEstado === status;
            return (
              <Chip
                key={status}
                label={`${estado.label} (${conteoPorEstado[status]})`}
                size="small"
                onClick={() => setFiltroEstado(activo ? null : status)}
                variant={activo ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  color: activo ? "#fff" : estado.color,
                  bgcolor: activo ? estado.color : "transparent",
                  borderColor: estado.color,
                  "&:hover": { bgcolor: activo ? estado.color : estado.bg },
                }}
              />
            );
          })}
        </Box>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={ocultarResueltos}
              onChange={(e) => setOcultarResueltos(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
              Ocultar los que tienen un pago aprobado
            </Typography>
          }
        />
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderColor: "#e8e8e8" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#fafafa" }}>
              <TableCell sx={headerSx}>Referencia</TableCell>
              <TableCell sx={headerSx}>Fecha creación</TableCell>
              <TableCell sx={headerSx} align="right">Monto MP</TableCell>
              <TableCell sx={headerSx}>Estado</TableCell>
              <TableCell sx={headerSx}>Detalle estado</TableCell>
              <TableCell sx={headerSx}>Medio</TableCell>
              <TableCell sx={headerSx}>ID MP</TableCell>
              <TableCell sx={headerSx} align="center">En sistema</TableCell>
              <TableCell sx={headerSx} align="center">Aprobado MP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datosPaginados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                    No hay registros para mostrar
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              datosPaginados.map((p) => {
                const estado = getEstadoMP(p.status);
                const enSistema = getEnSistema(p);
                return (
                  <TableRow hover key={p.id}>
                    <TableCell sx={cellSx}>{p.external_reference || "-"}</TableCell>
                    <TableCell sx={cellSx}>
                      {p.date_created ? moment(p.date_created).format("DD-MM-YYYY HH:mm") : "-"}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {p.transaction_amount ? `$${formatNumber(p.transaction_amount)}` : "-"}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Chip
                        label={estado.label}
                        size="small"
                        sx={{
                          bgcolor: estado.bg,
                          color: estado.color,
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, color: "#616161" }}>{p.status_detail || "-"}</TableCell>
                    <TableCell sx={cellSx}>{p.payment_method || "-"}</TableCell>
                    <TableCell sx={cellSx}>{p.id}</TableCell>
                    <TableCell sx={cellSx} align="center">
                      {enSistema === "soloDB" ? (
                        <Tooltip title="Existe en el sistema sin pago aprobado en MP (pestaña Sistema)">
                          <Chip label="Sí · sin MP" size="small" color="warning" sx={{ fontSize: "0.7rem", height: 22 }} />
                        </Tooltip>
                      ) : enSistema === "coincidencia" ? (
                        <Tooltip title="Existe en el sistema y tiene coincidencia con un pago aprobado">
                          <Chip label="Sí · conciliado" size="small" color="success" variant="outlined" sx={{ fontSize: "0.7rem", height: 22 }} />
                        </Tooltip>
                      ) : (
                        "No"
                      )}
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      {p.tieneAprobado ? (
                        <Tooltip title="La misma referencia tiene un pago aprobado en el período">
                          <Chip label="Sí" size="small" color="success" variant="outlined" sx={{ fontSize: "0.7rem", height: 22 }} />
                        </Tooltip>
                      ) : (
                        "No"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={9}
                count={datosVisibles.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  ".MuiTablePagination-select": { fontSize: "0.8rem" },
                  ".MuiTablePagination-displayedRows": { fontSize: "0.8rem" },
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
        <Typography variant="caption" color="textSecondary">
          Total mostrado: ${formatNumber(totalMonto)} · Los montos no aprobados no se suman a la conciliación
        </Typography>
      </Box>
    </Box>
  );
};

export default TablaNoAprobados;
